import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Router, Route, Switch, Redirect, withRouter } from 'react-router-dom';
import { IntlProvider, FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { get, isEmpty, isEqual } from 'lodash';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import history from '../helpers/history';
import asyncRoute from '../helpers/hoc/asyncRoute';
import rewardsRoutes from '../routes/rewards';
import { dispatchAuthTokenAction, dispatchLogoutAction } from '../actions/authentication';
import { notifyAuthTokenUpdated } from '../utils/authTokenSync';
import {
  fetchAppStartupInfoAction,
  clearApiErrorResponseAction,
  dispatchContextualHome,
  dispatchStartupInfoAndResourceDataAction,
  dispatchGuestLocationAction,
  dispatchAppResourcesAction,
  setLocaleAction,
} from '../actions/common';
import {
  dispatchCallbackUrlAction,
  fetchReferralCodeAction,
  generateReferralCodeAction,
} from '../actions/partner';
import { fetchGoogleMapsApi } from '../api/api';
import {
  matchRoutePatternWithWildcardSupport,
  extractWhitelabelSubdomain,
  isIosWebView,
  getDeviceType,
  compareVersions,
  getComponentVersionProps,
  isIosWebViewWrapper,
  isNativeWebView,
  getAppInStartupPhase,
  setIsApiRequestOnHold,
  fetchGeoLocation,
} from '../utils/helpers';

import BookingProvider from '../context/BookingProvider';

import { getItem, setItem } from '../utils/storage';
import {
  storageTypes,
  apiResponseCodes,
  transitionType,
  serverErrorCodes,
} from '../constants/globals';
import {
  getErrorTitle,
  getErrorDescription,
  getLocalityAddressFromAddressComponent,
} from '../selectors';
import { fetchAppResourcesAction } from '../actions/common';
import { fetchUserDetailsAction } from '../actions/app.action';
import { navigationalPanelRoutes, nativeEventType, brandModuleCodes } from '../config';
import { checkBrandModuleApplicable, extractDataFromNative } from '../helpers';
import { useDidUpdateEffect, useTheme } from '../utils/customHooks';
import { RewardsGreeting } from '../components/SplashScreen/RewardsGreeting';
import resourcesConfig from '../resources';
import { getLocalizedResources, normalizeLocale } from '../resources';
import ThemeContext from './ThemeContext';
import DefaultRegularFont from '../assets/fonts/OpenSans-Regular.ttf';
import DefaultSemiBoldFont from '../assets/fonts/OpenSans-SemiBold.ttf';
import DefaultBoldFont from '../assets/fonts/OpenSans-Bold.ttf';
import LocationPickerAnimation from '../components/LocationPickerAnimation';

import './index.scss';

const deviceType = getDeviceType();

// rewards container import
const Authentication = asyncRoute(() => import('./authentication'));
const Home = asyncRoute(() => import('./Home'));
const Account = asyncRoute(() => import('./Account'));
const Locations = asyncRoute(() => import('./Locations'));
const Referral = asyncRoute(() => import('./Referral'));
const AboutUs = asyncRoute(() => import('./AboutUs'));
const DeleteAccountSuccess = asyncRoute(() => import('./DeleteAccountSuccess'));
const LocationDetails = asyncRoute(() => import('./LocationDetails'));
const HelpAndSupport = asyncRoute(() => import('./HelpAndSupport'));
const VIPPassList = asyncRoute(() => import('./VIPPassList'));
const VIPPassDetails = asyncRoute(() => import('./VIPPassDetails'));
const Payment = asyncRoute(() => import('./Payment'));
const Location = asyncRoute(() => import('./Location'));
const Bookings = asyncRoute(() => import('./Bookings'));
const BookingDetails = asyncRoute(() => import('./BookingDetails'));
const Policies = asyncRoute(() => import('./Policies'));

// rewards components import

const NavigationPanel = asyncRoute(() => import('../components/NavigationPanel'));
const ExceptionComponent = asyncRoute(() => import('../components/ExceptionComponent'));
const NonExistentSubdomainFallback = asyncRoute(() =>
  import('./Fallback/NonExistentSubdomainFallback'),
);
const SessionExpiredPopover = asyncRoute(() => import('../components/SessionExpiredPopover'));

const isRewardsGreeterRendered = getItem(storageTypes.sessionStorage, 'isRewardsGreeterRendered');

const isLocationAnimationRendered = getItem(
  storageTypes.sessionStorage,
  'isLocationAnimationRendered',
);

const App = ({
  initialLocation,
  errorResponse,
  actions,
  resources,
  resourceContext,
  themeData,
  contextualHome,
  fontFamilyUrl,
  boldFontURL,
  semiBoldFontURL,
  userDetails,
  appStartupInfo,
  isWebApplicable,
  hasTiers,
  defaultWebRedirectionUrl,
  brandGuestId,
  applicableBrandModules,
  referralProgram,
  brandId,
  locale,
  ...props
}) => {
  const localizedResources = getLocalizedResources(resources, locale);
  const [activeTabContext, setActiveTabContext] = useState(rewardsRoutes.containers.home);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorContent, setErrorContent] = useState({});
  const [brandResources, setBrandResources] = useState(resources);
  const [showShareAppFeature, setShowShareAppFeature] = useState(true);
  const [startupInteractionDone, setStartupInteractionDone] = useState(false);
  const [nativeRouteName, setNativeRouteName] = useState('');
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [location, setLocation] = useState(initialLocation);

  const [greetingSplashActive, setGreetingSplashActive] = useState(
    isRewardsGreeterRendered ? false : true,
  );
  const [showNonExistentSubdomainFallback, setShowNonExistentSubdomainFallback] = useState(false);
  const [showLocationAnimation, setShowLocationAnimation] = useState(
    isLocationAnimationRendered ? false : true,
  );
  const [guestLocation, setGuestLocation] = useState({});
  const [locationPermissionStatus, setLocationPermissionStatus] = useState('');
  const [fetchingLocation, setFetchingLocation] = useState(true);
  const [fetchingLocality, setFetchingLocality] = useState(false);
  const [localityDetails, setLocalityDetails] = useState({});
  const [isInitialLocationFetched, setIsInitialLocationFetched] = useState(false);
  const pathname = get(location, 'pathname', '');
  const showGreeterScreen = get(brandResources, 'showGreeterScreen', true);
  const isThemeApplied = useTheme('rewards-container', themeData);
  const callbackUrlRef = useRef();
  const resourceContextRef = useRef(resourceContext);
  const localeRef = useRef(normalizeLocale(locale));

  const isReferralApplicable = checkBrandModuleApplicable({
    applicableBrandModules,
    moduleName: brandModuleCodes.referral,
  });

  useEffect(() => {
    if (!isNativeWebView) {
      if (!isWebApplicable) {
        setGreetingSplashActive(false);
      }
    }
  }, [isWebApplicable]);

  useEffect(() => {
    const handleFetchGeoLocationSuccess = (position) => {
      const draftLocationCoordinates = {
        lat: get(position, 'coords.latitude'),
        lng: get(position, 'coords.longitude'),
      };
      setItem(storageTypes.localStorage, 'geoLocation', draftLocationCoordinates);
      setGuestLocation(draftLocationCoordinates);
      setLocationPermissionStatus('granted');
      setFetchingLocation(false);
    };
    const handleFetchGeoLocationFailure = () => {
      setLocationPermissionStatus('denied');
      setFetchingLocation(false);
    };
    if (!isEmpty(userDetails)) {
      if (isNativeWebView) {
        const locationData = {
          permissionType: { type: nativeEventType.location },
        };
        window.ReactNativeWebView.postMessage(JSON.stringify(locationData));
      } else {
        fetchGeoLocation(handleFetchGeoLocationSuccess, handleFetchGeoLocationFailure);
      }
    }
  }, [userDetails]);

  useEffect(() => {
    // The native shell owns the EXPERT resource request while embedded. A
    // second web request can finish later and overwrite the native payload.
    if (!isNativeWebView) actions.fetchAppResources();
  }, []);

  useEffect(() => {
    if (
      !isNativeWebView ||
      !window.ReactNativeWebView ||
      !resourceContext ||
      Object.keys(resourceContext).length === 0
    ) {
      return;
    }

    // Publish the complete, unlocalized resource context. Native performs a
    // deep equality check before dispatching it, preventing an echo loop when
    // this change originally came from native.
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        eventType: {
          type: nativeEventType.appResourcesUpdatedFromWeb,
          data: {
            resources: resourceContext,
            locale,
          },
        },
      }),
    );
  }, [resourceContext, locale]);

  useEffect(() => {
    resourceContextRef.current = resourceContext;
    localeRef.current = normalizeLocale(locale);
  }, [resourceContext, locale]);

  useEffect(() => {
    if (!isNativeWebView || !window.ReactNativeWebView) return undefined;

    // Resource delivery must not depend on a whitelabel subdomain. The native
    // WebView can load the partner app through different hosts during testing.
    const handleNativeResourceMessage = (message) => {
      try {
        const rawMessage = get(message, 'data', '');
        if (!rawMessage) return;
        const parsedMessage = JSON.parse(rawMessage);
        if (get(parsedMessage, 'eventType') !== nativeEventType.appResourcesUpdated) return;
        handleAppResourcesUpdated(get(parsedMessage, 'data', {}));
      } catch (error) {
        console.warn('[Resources] Unable to parse native resource payload', error);
      }
    };

    window.addEventListener('message', handleNativeResourceMessage);
    document.addEventListener('message', handleNativeResourceMessage);
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        eventType: {
          type: nativeEventType.appResourcesReady,
          data: { locale: localeRef.current },
        },
      }),
    );

    return () => {
      window.removeEventListener('message', handleNativeResourceMessage);
      document.removeEventListener('message', handleNativeResourceMessage);
    };
  }, []);

  useEffect(() => {
    handleFetchLocationPermissionStatus();
  }, [guestLocation]);

  const handleFetchLocationPermissionStatus = () => {
    if (!isNativeWebView) {
      if (navigator.permissions) {
        navigator.permissions
          .query({ name: 'geolocation' })
          .then((result) => {
            console.log('status', { result });
            setLocationPermissionStatus(result.state);
          })
          .catch((error) => {
            console.log('status', { error });

            setLocationPermissionStatus('denied');
          });
      }
    } else {
      // for Native we'll get the permission status along with the location permission event
    }
  };

  // Fetch locality details when location coordinates are available
  useEffect(() => {
    const latitude = get(guestLocation, 'lat', get(guestLocation, 'latitude'));
    const longitude = get(guestLocation, 'lng', get(guestLocation, 'longitude'));
    if (
      latitude &&
      longitude &&
      !fetchingLocality &&
      isEmpty(localityDetails) &&
      isEqual(locationPermissionStatus, 'granted') &&
      !isInitialLocationFetched
    ) {
      setIsInitialLocationFetched(true);
      fetchLocalityDetails(latitude, longitude);
    }
  }, [guestLocation, locationPermissionStatus]);

  useEffect(() => {
    if (isNativeWebView) {
      window.addEventListener('NewLocationSet', handleLocationEventFromNative);
    }

    return () => {
      if (isNativeWebView) {
        window.removeEventListener('NewLocationSet', handleLocationEventFromNative);
      }
    };
  }, []);

  const handleLocationEventFromNative = () => {
    const draftLocation = getItem(storageTypes.localStorage, 'geoLocation');
    const draftStatus = getItem(storageTypes.localStorage, 'geoLocationPermissionStatus');
    setGuestLocation(draftLocation);
    setLocationPermissionStatus(draftStatus);
    setFetchingLocation(false);
  };

  const requestPartnerLocation = () => {
    setFetchingLocation(true);

    if (isNativeWebView) {
      const type = isEqual(locationPermissionStatus, 'denied')
        ? nativeEventType.openLocationSettings
        : nativeEventType.location;
      const payload =
        type === nativeEventType.location
          ? { permissionType: { type } }
          : { eventType: { type } };
      window.ReactNativeWebView.postMessage(JSON.stringify(payload));
      setFetchingLocation(false);
      return;
    }

    fetchGeoLocation(
      (position) => {
        const locationCoordinates = {
          lat: get(position, 'coords.latitude'),
          lng: get(position, 'coords.longitude'),
        };
        setItem(storageTypes.localStorage, 'geoLocation', locationCoordinates);
        setGuestLocation(locationCoordinates);
        setLocationPermissionStatus('granted');
        actions.dispatchGuestLocation({ guestLocation: locationCoordinates });
        setFetchingLocation(false);
      },
      () => {
        setLocationPermissionStatus('denied');
        setFetchingLocation(false);
      },
    );
  };

  useEffect(() => {
    setItem(storageTypes.sessionStorage, 'isLocationAnimationRendered', true);
    setItem(storageTypes.sessionStorage, 'isRewardsGreeterRendered', true);
    const guestLoginData = getItem(storageTypes.localStorage, 'GuestLoginData');
    const guestId = get(guestLoginData, 'id', '');
    const subDomain = extractWhitelabelSubdomain();
    const { pathname, search } = location;
    let callbackUrl = search ? `${pathname}${search}` : pathname;
    callbackUrlRef.current = callbackUrl;
    let activePathNames = pathname && pathname.split('/');
    let activePathName = activePathNames[2];
    if (subDomain) {
      const appStartupReqBody = {
        subdomain: subDomain,
        includeModules: ['reward', 'brand', 'resource'],
      };
      if (isNativeWebView) {
        const startupInfoData = {
          eventType: { type: nativeEventType.appStartupInfo },
        };
        window.ReactNativeWebView.postMessage(JSON.stringify(startupInfoData));
        // fetch Startup Info
        // fetchStartupInfoData({ appStartupReqBody });
        // event listner for ios
        let dataFromApp = '';
        window.addEventListener('message', function (msg) {
          dataFromApp = get(msg, 'data', '');
          if (!isEmpty(dataFromApp)) {
            dataFromApp = dataFromApp && JSON.parse(dataFromApp);
            const { eventType, data } = dataFromApp;

            if (isEqual(eventType, nativeEventType.appStartupInfo) && !isEmpty(data)) {
              // dispatch app startup data into redux
              actions.dispatchStartupInfoAndResourceData(data);
            } else {
              // fetch Startup Info
              // fetchStartupInfoData({ appStartupReqBody });
            }
            if (isEqual(eventType, nativeEventType.brandGuestData) && !isEmpty(data)) {
              actions.dispatchRewardGuestProfile(data);
            }
            if (isEqual(eventType, nativeEventType.sessionHandled)) {
              setIsApiRequestOnHold(false);
            }
          } else {
            // fetch Startup Info
            // fetchStartupInfoData({ appStartupReqBody });
          }
        });

        // event listner for android
        document.addEventListener('message', function (msg) {
          dataFromApp = get(msg, 'data', '');

          if (!isEmpty(dataFromApp)) {
            dataFromApp = dataFromApp && JSON.parse(dataFromApp);
            const { eventType, data } = dataFromApp;
            if (isEqual(eventType, nativeEventType.appStartupInfo) && !isEmpty(data)) {
              // dispatch app startup data into redux
              actions.dispatchStartupInfoAndResourceData(data);
              setStartupInteractionDone(true);
            } else {
              // fetch Startup Info
              // fetchStartupInfoData({ appStartupReqBody });
            }
            if (isEqual(eventType, nativeEventType.brandGuestData) && !isEmpty(data)) {
              actions.dispatchRewardGuestProfile(data);
            }
            if (isEqual(eventType, nativeEventType.sessionHandled)) {
              setIsApiRequestOnHold(false);
            }
          } else {
            // fetch Startup Info
            // fetchStartupInfoData({ appStartupReqBody });
          }
        });
      }

      if (isNativeWebView) {
        // ensure window.ReactNativeWebView is there, otherwise, web app might crash if is not there
        const nativeAppUpdateAvailable = {
          eventType: { type: nativeEventType.nativeAppUpdateAvailable },
        };
        window.ReactNativeWebView.postMessage(JSON.stringify(nativeAppUpdateAvailable));
        const appVersionData = {
          eventType: { type: nativeEventType.appVersion },
        };
        window.ReactNativeWebView.postMessage(JSON.stringify(appVersionData));

        // add event listeners for catch the event from web view
        // event listner for ios
        window.addEventListener('message', function (message) {
          extractDataFromNative({
            message,
            history,
            actions,
            handleNativeBackCTA,
            handleLogout,
            handleNativeRouteName,
            handleAuthTokenUpdated,
          });
        });
        // event listner for android
        document.addEventListener('message', function (message) {
          extractDataFromNative({
            message,
            history,
            handleNativeBackCTA,
            handleLogout,
            handleNativeRouteName,
            handleAuthTokenUpdated,
          });
        });
      }
    } else {
      history.push(callbackUrl);
    }

    // ignore greeter for get app
    const greeterExceptionalRoutes = [];

    if (greeterExceptionalRoutes.includes(activePathName)) {
      setGreetingSplashActive(false);
    }

    // component un mount logic
    return () => {
      actions.dispatchContextualHome('');
    };
  }, []);

  useEffect(() => {
    const guestLoginData = getItem(storageTypes.localStorage, 'GuestLoginData');
    const guestId = get(guestLoginData, 'id', '');
    const { pathname, search } = location;
    let callbackUrl = callbackUrlRef.current
      ? callbackUrlRef.current
      : search
      ? `${pathname}${search}`
      : pathname;
    let activePathName = callbackUrl && callbackUrl.split('?')[0].split('/');
    activePathName = activePathName[2];

    //check for brand guest user
    if (isEmpty(userDetails)) {
      if (!isEmpty(guestId)) {
        actions.fetchUserDetails({ userId: guestId }).then((response) => {
          if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
            const userDetails = get(response, 'data', {});
            if (isEmpty(userDetails)) {
              history.push(
                `/${rewardsRoutes.containers.authentication}/${
                  rewardsRoutes.authentication.signUp
                }${search ? search : ''}`,
              );
            }
          }
        });
      } else {
        setShowLocationAnimation(false);
        actions.dispatchCallbackUrl({ callbackUrl }).then(() => {
          history.push(
            `/${rewardsRoutes.containers.authentication}/${rewardsRoutes.authentication.signUp}${
              search ? search : ''
            }`,
          );
        });
      }
    } else {
      actions
        .fetchUserDetails({
          userId: get(userDetails, 'id', ''),
        })
        .then((response) => {
          if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
            if (isReferralApplicable && !isEmpty(referralProgram)) {
              handleFetchReferralCode({
                brandGuestId: get(response, 'data.id', ''),
                brandId: get(appStartupInfo, 'brandId'),
              });
            }
            if (isNativeWebView) {
              // ensure window.ReactNativeWebView is there, otherwise, web app might crash if is not there
              const data = {
                eventType: { type: 'brandGuestData', data: get(response, 'data', {}) },
              };
              window.ReactNativeWebView.postMessage(JSON.stringify(data));
            }
            if (callbackUrl) {
              history.push(callbackUrl);
            }
          }
        });
    }
  }, []);

  useEffect(() => {
    if (isNativeWebView) {
      let activePathName = pathname && pathname.split('/');
      activePathName = activePathName[2];
      if (!isEqual(activePathName, rewardsRoutes.containers.authentication)) {
        const nativeAppUpdateAvailable = getItem(
          storageTypes.sessionStorage,
          'nativeAppUpdateAvailable',
        );
        if (!nativeAppUpdateAvailable) {
          const appVersion = getItem(storageTypes.sessionStorage, 'appVersion');
          const appUpdateConfigObj =
            brandResources &&
            brandResources.appUpdate &&
            brandResources.appUpdate.find((appConfig) => appConfig.platform === deviceType);
          const minReqAppVersion = get(appUpdateConfigObj, 'minRequiredAppVersion');
          const defaultAppVersion = get(appUpdateConfigObj, 'currentAppVersion');
          const webFooterApplicableVersion = get(appUpdateConfigObj, 'webFooterApplicableVersion');
          const currentAppVersion = appVersion ? appVersion : defaultAppVersion;

          if (!isEmpty(currentAppVersion)) {
            if (
              webFooterApplicableVersion &&
              compareVersions(currentAppVersion, webFooterApplicableVersion) < 0
            ) {
              // Keep unsupported native-only features disabled on older app versions.
              if (!isIosWebView) {
                setShowShareAppFeature(false);
              }
            }
          }
        }
      }
    }
  }, [pathname]);

  let pagesExistInNative = getItem(storageTypes.localStorage, 'pagesInNative') || [];

  useEffect(() => {
    if (
      (!greetingSplashActive && showGreeterScreen) ||
      !showGreeterScreen ||
      !isEmpty(pagesExistInNative)
    ) {
      if (isNativeWebView) {
        // ensure window.ReactNativeWebView is there, otherwise, web app might crash if is not there

        const data = {
          eventType: { type: 'greeterScreenRendered' },
        };
        window.ReactNativeWebView.postMessage(JSON.stringify(data));
      }
    }
  }, [greetingSplashActive, showGreeterScreen]);

  useDidUpdateEffect(() => {
    setBrandResources(resources);
  }, [resources]);

  useEffect(() => {
    if (isNativeWebView) {
      // ensure window.ReactNativeWebView is there, otherwise, web app might crash if is not there
      const data = {
        eventType: { type: 'routeName', data: location },
      };
      window.ReactNativeWebView.postMessage(JSON.stringify(data));
    }
  }, [location]);

  useEffect(() => {
    const defaultFontURL = DefaultRegularFont;
    const defaultFont = 'DefaultFont';
    const fontURL = !isEmpty(fontFamilyUrl) ? fontFamilyUrl : DefaultRegularFont;
    const RegularFont = 'RegularFont';
    const bFontURL = !isEmpty(boldFontURL) ? boldFontURL : DefaultBoldFont;
    const BoldFont = 'BoldFont';
    const sBFontURL = !isEmpty(semiBoldFontURL) ? semiBoldFontURL : DefaultSemiBoldFont;
    const SemiBoldFont = 'SemiBoldFont';
    var newStyle = document.createElement('style');
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
    font-family: ' +
          RegularFont +
          ";\
    src: url('" +
          fontURL +
          "') format('woff');\
}\
",
      ),
    );
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
  font-family: ' +
          BoldFont +
          ";\
  src: url('" +
          bFontURL +
          "') format('woff');\
}\
",
      ),
    );
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
  font-family: ' +
          SemiBoldFont +
          ";\
  src: url('" +
          sBFontURL +
          "') format('woff');\
}\
",
      ),
    );
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
  font-family: ' +
          defaultFont +
          ";\
  src: url('" +
          defaultFontURL +
          "') format('woff');\
}\
",
      ),
    );
    document.head.appendChild(newStyle);
  }, [fontFamilyUrl, boldFontURL, semiBoldFontURL]);

  useDidUpdateEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }); // eslint-disable-line
    if (isNativeWebView && !isIosWebView) {
      // handling for android devices scroll(as above event stops scrolls in middle due to native webview event handling)
      setTimeout(() => {
        if (window.pageYOffset > 0) {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }
      }, 200);
    }
  }, [pathname]);

  useEffect(() => {
    if (
      !isEmpty(errorResponse) &&
      ![
        'OTP_INVALID',
        'DEAL_ALREADY_ASSIGNED',
        'USER_BLOCKED',
        'PASS_SUBSCRIBE_ERROR',
        'DELIVERY_MIN_VALUE_ERR',
        serverErrorCodes.invalidToken,
      ].includes(get(errorResponse, 'code'))
    ) {
      const { code, message } = errorResponse;
      const draftErrorContent = {
        title: getErrorTitle({ source: brandResources, code }),
        description: getErrorDescription({ source: brandResources, code, message }),
      };
      setShowErrorPopup(true);
      setErrorContent(draftErrorContent);
    }
    if (
      !isEmpty(errorResponse) &&
      isEqual(get(errorResponse, 'code'), serverErrorCodes.invalidToken)
    ) {
      handleSessionExpiredErrorResponse(errorResponse);
    }
  }, [errorResponse]);

  const handleLogout = (props) => {
    const prefilledData = get(props, 'prefilledData', {});
    localStorage.setItem('GuestLoginData', JSON.stringify({}));
    localStorage.setItem('AuthData', JSON.stringify({}));
    actions.dispatchLogout();
    history.push(
      `/${rewardsRoutes.containers.authentication}/${
        !isEmpty(prefilledData)
          ? rewardsRoutes.authentication.signIn
          : rewardsRoutes.authentication.signUp
      }`,
      { prefilledData },
    );
  };

  const handleSessionExpiredErrorResponse = (errorResponse) => {
    if (isNativeWebView) {
      // send session expired event to native to let native handle the session expired
      const sessionExpiredData = {
        eventType: { type: nativeEventType.sessionExpired },
      };
      window.ReactNativeWebView.postMessage(JSON.stringify(sessionExpiredData));
      return;
    }
    setIsApiRequestOnHold(false);
    const isAppInStartupPhase = getAppInStartupPhase();
    if (isAppInStartupPhase) {
      const prefilledData = {
        primaryPhoneNumber: get(userDetails, 'primaryMobileNumber'),
        errorResponse,
      };
      console.log('prefilledData', prefilledData);
      handleLogout({ prefilledData });
      actions.clearApiErrorResponse();
    } else {
      setShowSessionExpired(true);
    }
  };

  const handleCloseSessionExpired = () => {
    setShowSessionExpired(false);
    actions.clearApiErrorResponse();
    const prefilledData = {
      primaryPhoneNumber: get(userDetails, 'primaryMobileNumber'),
      errorResponse,
    };
    handleLogout({ prefilledData });
  };

  const handleNativeRouteName = (routeName) => {
    setNativeRouteName(routeName);
  };

  // Function to fetch locality details using reverse geocoding
  const fetchLocalityDetails = async (latitude, longitude) => {
    if (!latitude || !longitude) return;
    setFetchingLocality(true);
    try {
      const response = await fetchGoogleMapsApi({ latitude, longitude });
      if (get(response, 'data.results.length', 0) > 0) {
        const addressInfo = get(response, 'data.results.0', {});
        const addressComponents = get(addressInfo, 'address_components', []);
        const formattedAddress = getLocalityAddressFromAddressComponent(addressComponents);
        const { locality, subLocality, administrativeArea } = formattedAddress || {};

        const addressName = subLocality;
        const address = `${locality ? locality + ', ' : ''}${administrativeArea || ''}`;

        actions.dispatchGuestLocation({
          guestLocation: { latitude, longitude, addressName, address },
        });

        setLocalityDetails(formattedAddress);
      }
    } catch (error) {
      console.error('Error fetching locality details:', error);
    } finally {
      setFetchingLocality(false);
      setFetchingLocation(false);
    }
  };

  const fetchStartupInfoData = ({ appStartupReqBody }) =>
    actions.fetchAppStartupInfo({ appStartupReqBody }).then((response) => {
      if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
        actions.dispatchContextualHome(`/${rewardsRoutes.containers.home}`);
        setStartupInteractionDone(true);
      } else if (!isEqual(get(response, 'data.code'), serverErrorCodes.invalidToken)) {
        // incase if sub domain is not resolved
        setShowNonExistentSubdomainFallback(true);
      }
    });

  const handleFetchReferralCode = async ({ brandGuestId, brandId }) => {
    await actions.fetchReferralCode({ brandGuestId }).then((response) => {
      if (apiResponseCodes.NOT_FOUND.includes(get(response, 'status'))) {
        if (!isEmpty(brandGuestId) && !isEmpty(brandId)) {
          handleGenerateReferralCode({ brandGuestId, brandId });
        }
      }
    });
  };

  const handleGenerateReferralCode = async ({ brandGuestId, brandId }) => {
    await actions.generateReferralCode({
      generateReferralCodeReqBody: {
        brandGuestId,
        brandId,
      },
    });
  };

  const getActiveTabContext = (pathname) => {
    let currentActiveTabContext = activeTabContext;
    const paths = pathname.trim().split('/').filter(Boolean); // removes empty strings

    if (paths.length > 0) {
      currentActiveTabContext = paths[0]; // first part of the path e.g. "account"
    }

    return currentActiveTabContext;
  };
  const onCloseExceptionComponent = () => {
    actions.clearApiErrorResponse().then(() => {
      setShowErrorPopup(false);
      setErrorContent({});
    });
  };

  const getActiveRoute = (pathname) => {
    if (pathname) {
      const paths = pathname.trim().split('/') || [];
      let route = '';
      route = paths.slice(1, paths.length).join('/');
      return route;
    }
  };

  const activeRoute = getActiveRoute(get(location, 'pathname', ''));

  const handleNavTabClick = (type) => {
    switch (type) {
      case rewardsRoutes.containers.home:
        return history.push(`/${rewardsRoutes.containers.home}`);
      case rewardsRoutes.containers.locations:
        return history.push(`/${rewardsRoutes.containers.locations}`);
      case rewardsRoutes.containers.account:
        return history.push(`/${rewardsRoutes.containers.account}`);
      case rewardsRoutes.containers.bookings:
        return history.push(`/${rewardsRoutes.containers.bookings}`);
      default:
        return;
    }
  };

  const handleNonExistentSubdomainClick = () => {
    history.push(`/${rewardsRoutes.containers.home}`);
    window.location.reload();
  };

  const handleAuthTokenUpdated = (nextAuthData = {}) => {
    if (isEmpty(get(nextAuthData, 'accessToken', ''))) return;

    const currentAuthData = getItem(storageTypes.localStorage, 'AuthData', {});
    const mergedAuthData = { ...currentAuthData, ...nextAuthData };
    actions.dispatchAuthToken(mergedAuthData);
    notifyAuthTokenUpdated(mergedAuthData);
  };

  const handleAppResourcesUpdated = (payload = {}) => {
    if (!payload || typeof payload !== 'object') return;

    // New native builds send both the EXPERT resources and selected locale.
    // Keep accepting the legacy raw-resources payload during rollout.
    const nextResources = get(payload, 'resources', payload);
    const nextLocale = normalizeLocale(get(payload, 'locale'));
    const resourcesChanged = !isEqual(nextResources, resourceContextRef.current);
    const localeChanged = nextLocale !== localeRef.current;

    if (nextResources && typeof nextResources === 'object' && resourcesChanged) {
      resourceContextRef.current = nextResources;
      actions.dispatchAppResources(nextResources);
    }
    if (localeChanged) {
      localeRef.current = nextLocale;
      actions.setLocale(nextLocale);
    }
    console.info('[Resources] Native resource payload applied', {
      locale: nextLocale,
      resourcesChanged,
      localeChanged,
      resourceKeyCount: Object.keys(nextResources || {}).length,
    });
    if (isNativeWebView && window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          eventType: {
            type: nativeEventType.appResourcesApplied,
            data: {
              locale: nextLocale,
              resourceKeyCount: Object.keys(nextResources || {}).length,
            },
          },
        }),
      );
    }
  };

  // Handle native back button
  const handleNativeBackCTA = (key) => {
    const backButtonElement = document.getElementById(key);
    if (backButtonElement !== null) {
      backButtonElement.click();
    } else {
      const entryRoutes = [rewardsRoutes.authentication, rewardsRoutes.containers.home];
      const pathname = get(location, 'pathname');
      if (!entryRoutes.some((entryRoute) => pathname.includes(entryRoute))) {
        history.push(`/${rewardsRoutes.containers.home}`);
      }
    }
  };

  const userName = get(userDetails, 'name', '');
  const showGreeterText = get(brandResources, 'showGreeterText', true);
  const greeterVisibilityTimeInMS = get(brandResources, 'greeterVisibilityTimeInMS', 3000);
  const greeterStaticInfo = get(brandResources, 'staticPages', []).find((page) =>
    isEqual(page.route, 'greeter'),
  );
  const greeterHTML = get(greeterStaticInfo, 'content', '');

  const LocationChangeCallback = withRouter(({ location: routerLocation }) => {
    // sync location state with React Router's location from Route context
    useEffect(() => {
      setLocation(routerLocation);
    }, [routerLocation]);

    return null;
  });

  const renderRoutes = () => (
    <>
      {showNonExistentSubdomainFallback ? (
        <NonExistentSubdomainFallback
          resources={{
            title: (
              <FormattedMessage
                id="nonExistSubdomainFallbackTitle"
                defaultMessage={resourcesConfig.nonExistSubdomainFallbackTitle}
              />
            ),
            description: (
              <FormattedMessage
                id="nonExistSubdomainFallbackDescription"
                defaultMessage={resourcesConfig.nonExistSubdomainFallbackDescription}
              />
            ),
            atnBtn: (
              <FormattedMessage
                id="nonExistSubdomainFallbackCTA"
                defaultMessage={resourcesConfig.nonExistSubdomainFallbackCTA}
              />
            ),
          }}
          handleGoHome={() => handleNonExistentSubdomainClick()}
        />
      ) : (
        <Router history={history}>
          <React.Fragment>
            {/* location change callback for updating the location state in the container*/}
            <LocationChangeCallback />
            <Switch location={location} key={location.pathname}>
              <Route
                exact
                path="/"
                render={() =>
                  location.pathname === '/' ? (
                    <Redirect to={`/${rewardsRoutes.containers.home}`} />
                  ) : null
                }
              />
              <Route
                path={`/${rewardsRoutes.containers.authentication}`}
                component={Authentication}
              />

              <Route path={`/${rewardsRoutes.containers.home}`} component={Home} />

              <Route
                path={`/${rewardsRoutes.containers.account}`}
                render={(props) => <Account showShareAppFeature={showShareAppFeature} {...props} />}
              />

              <Route path={`/${rewardsRoutes.containers.referral}`} component={Referral} />
              <Route exact path={`/${rewardsRoutes.containers.locations}`} component={Locations} />
              <Route exact path={`/${rewardsRoutes.containers.location}`} component={Location} />
              <Route
                path={`/${rewardsRoutes.containers.locations}/:outletId`}
                component={LocationDetails}
              />
              <Route path={`/${rewardsRoutes.containers.aboutUs}`} component={AboutUs} />
              <Route
                path={`/${rewardsRoutes.containers.deleteAccountSuccess}`}
                component={DeleteAccountSuccess}
              />
              <Route path={`/${rewardsRoutes.containers.helpSupport}`} component={HelpAndSupport} />
              <Route path={`/${rewardsRoutes.containers.policies}`} component={Policies} />
              <Route path={`/${rewardsRoutes.containers.vipPassList}`} component={VIPPassList} />
              <Route
                path={`/${rewardsRoutes.containers.vipPassDetail}/:passCode`}
                component={VIPPassDetails}
              />

              <Route
                exact
                path={`/${rewardsRoutes.containers.bookingDetails}/:bookingId`}
                component={BookingDetails}
              />
              <Route exact path={`/${rewardsRoutes.containers.bookings}`} component={Bookings} />
              <Route exact path={`/${rewardsRoutes.containers.payment}`} component={Payment} />
            </Switch>
          </React.Fragment>
        </Router>
      )}
    </>
  );
  return (
    <div
      className={classNames('rewards', {
        'in-app-style': isIosWebView,
      })}
      id="rewards-container"
    >
      {/* render greeting splash screen here */}
      <BookingProvider>
        <ThemeContext.Provider
          value={{
            themeContext: rewardsRoutes.rewards,
            nativeRouteName: nativeRouteName,
            guestLocation,
            fetchingLocation,
            locationPermissionStatus,
            requestPartnerLocation,
          }}
        >
          <IntlProvider
            messages={localizedResources}
            locale={normalizeLocale(locale)}
            defaultLocale="hi"
          >
            {greetingSplashActive &&
              isThemeApplied &&
              showGreeterScreen &&
              !isEmpty(brandResources) &&
              isEmpty(pagesExistInNative) && (
                <RewardsGreeting
                  active={greetingSplashActive}
                  deactivate={() => setGreetingSplashActive(false)}
                  splashBgImage={get(brandResources, 'greeterSplashScreen', '')}
                  loggedInGuestName={userName}
                  showTimeSalutation
                  showGreeterText={showGreeterText}
                  splashDuration={greeterVisibilityTimeInMS}
                  brandSprite={get(brandResources, 'brandSprite', '')}
                  showStaticGreeter={get(brandResources, 'showStaticGreeter', false)}
                  greeterHTML={greeterHTML}
                />
              )}
            {/* {showLocationAnimation && (
              <LocationPickerAnimation
                history={history}
                setShowLocationAnimation={setShowLocationAnimation}
              />
            )} */}
            {isThemeApplied && (
              <>
                {!isEmpty(pagesExistInNative) ? (
                  renderRoutes()
                ) : (
                  <SwitchTransition component={null}>
                    <CSSTransition
                      key={location.pathname}
                      timeout={300}
                      classNames={transitionType}
                    >
                      {renderRoutes()}
                    </CSSTransition>
                  </SwitchTransition>
                )}
                {matchRoutePatternWithWildcardSupport(
                  navigationalPanelRoutes,
                  activeRoute,
                ) && !isNativeWebView && (
                  <NavigationPanel
                    onNavTabClick={(type) => {
                      handleNavTabClick(type);
                    }}
                    activeTabContext={getActiveTabContext(get(location, 'pathname'))}
                  />
                )}
                {showErrorPopup && (
                  <ExceptionComponent
                    pageType="HalfSize"
                    openSegmentPopup={showErrorPopup}
                    dismissSegmentPopup={onCloseExceptionComponent}
                    title={get(errorContent, 'title')}
                    description={get(errorContent, 'description')}
                  />
                )}
                {showSessionExpired && (
                  <SessionExpiredPopover
                    open={showSessionExpired}
                    onCTAClick={handleCloseSessionExpired}
                  />
                )}
              </>
            )}
          </IntlProvider>
        </ThemeContext.Provider>
      </BookingProvider>
    </div>
  );
};

const mapStateToProps = (state) => ({
  appStartupInfo: get(state, 'common.appStartupInfo', {}),
  brandId: get(state, 'common.appStartupInfo.brandId', ''),
  contextualHome: get(state, 'common.contextualHome', ''),
  errorResponse: get(state, 'common.errorResponse', {}),
  resources: get(state, 'common.resources', {}),
  resourceContext: get(state, 'common.resourcesByLocale', {}),
  themeData: get(state, 'common.resources.themeData', {}),
  fontFamilyUrl: get(state, 'common.resources.themeData.fontFamilyUrl', ''),
  semiBoldFontURL: get(state, 'common.resources.themeData.semiBoldFontURL', ''),
  boldFontURL: get(state, 'common.resources.themeData.boldFontURL', ''),
  outletDetails: get(state, 'common.outletDetails', {}),
  userDetails: get(state, 'authentication.userDetails', {}),
  hasTiers: get(state, 'common.appStartupInfo.rewardProgram.hasTiers', false),
  defaultWebRedirectionUrl: get(state, 'common.resources.defaultWebRedirectionUrl', ''),
  referralProgram: get(state, 'common.appStartupInfo.rewardProgram.referralProgram.program', {}),
  applicableBrandModules: get(state, 'common.modules', []),
  locale: get(state, 'common.locale', 'hi'),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchAppStartupInfo: fetchAppStartupInfoAction,
      clearApiErrorResponse: clearApiErrorResponseAction,
      fetchUserDetails: fetchUserDetailsAction,
      dispatchContextualHome,
      dispatchCallbackUrl: dispatchCallbackUrlAction,
      dispatchStartupInfoAndResourceData: dispatchStartupInfoAndResourceDataAction,
      dispatchLogout: dispatchLogoutAction,
      dispatchAuthToken: dispatchAuthTokenAction,
      fetchReferralCode: fetchReferralCodeAction,
      generateReferralCode: generateReferralCodeAction,
      dispatchGuestLocation: dispatchGuestLocationAction,
      fetchAppResources: fetchAppResourcesAction,
      dispatchAppResources: dispatchAppResourcesAction,
      setLocale: setLocaleAction,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
